import { z } from "zod";

import {
  IsoTimestampSchema,
  RequiredShortTextSchema,
  ShortTextSchema,
  SortOrderSchema,
  UserTextSchema,
  UUIDSchema,
} from "./shared";

export const AccentColorSchema = z
  .string()
  .regex(/^#[0-9a-f]{6}$/i, "Accent must be a six-digit hex colour");

const CollectionMutableShape = {
  name: RequiredShortTextSchema.max(80),
  description: UserTextSchema.max(2_000).optional(),
  icon: z.string().trim().min(1).max(80).optional(),
  accent: AccentColorSchema.optional(),
  sortOrder: SortOrderSchema.default(0),
  archivedAt: IsoTimestampSchema.optional(),
} satisfies z.ZodRawShape;

export const CollectionCreateSchema = z.object(CollectionMutableShape).strict();

export const CollectionSchema = z
  .object({
    id: UUIDSchema,
    ...CollectionMutableShape,
    normalizedName: z.string().min(1).max(80),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    deletedAt: IsoTimestampSchema.optional(),
  })
  .strict();

export const CollectionUpdateSchema = z
  .object({
    name: RequiredShortTextSchema.max(80).optional(),
    description: UserTextSchema.max(2_000).optional(),
    icon: z.string().trim().min(1).max(80).optional(),
    accent: AccentColorSchema.optional(),
    sortOrder: SortOrderSchema.optional(),
    archivedAt: IsoTimestampSchema.optional(),
  })
  .strict();

const TagMutableShape = {
  name: RequiredShortTextSchema.max(80),
  accent: AccentColorSchema.optional(),
  sortOrder: SortOrderSchema.default(0),
} satisfies z.ZodRawShape;

export const TagCreateSchema = z.object(TagMutableShape).strict();

export const TagSchema = z
  .object({
    id: UUIDSchema,
    ...TagMutableShape,
    normalizedName: z.string().min(1).max(80),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    deletedAt: IsoTimestampSchema.optional(),
  })
  .strict();

export const TagUpdateSchema = z
  .object({
    name: RequiredShortTextSchema.max(80).optional(),
    accent: AccentColorSchema.optional(),
    sortOrder: SortOrderSchema.optional(),
  })
  .strict();

const PersonMutableShape = {
  displayName: RequiredShortTextSchema.max(120),
  relationship: ShortTextSchema.max(120).optional(),
  notes: UserTextSchema.optional(),
  lastRememberedAt: IsoTimestampSchema.optional(),
  sortOrder: SortOrderSchema.default(0),
  archivedAt: IsoTimestampSchema.optional(),
} satisfies z.ZodRawShape;

export const PersonCreateSchema = z.object(PersonMutableShape).strict();

export const PersonSchema = z
  .object({
    id: UUIDSchema,
    ...PersonMutableShape,
    normalizedName: z.string().min(1).max(120),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    deletedAt: IsoTimestampSchema.optional(),
  })
  .strict();

export const PersonUpdateSchema = z
  .object({
    displayName: RequiredShortTextSchema.max(120).optional(),
    relationship: ShortTextSchema.max(120).optional(),
    notes: UserTextSchema.optional(),
    lastRememberedAt: IsoTimestampSchema.optional(),
    sortOrder: SortOrderSchema.optional(),
    archivedAt: IsoTimestampSchema.optional(),
  })
  .strict();

export type Collection = z.output<typeof CollectionSchema>;
export type CreateCollectionInput = z.input<typeof CollectionCreateSchema>;
export type UpdateCollectionInput = z.input<typeof CollectionUpdateSchema>;
export type Tag = z.output<typeof TagSchema>;
export type CreateTagInput = z.input<typeof TagCreateSchema>;
export type UpdateTagInput = z.input<typeof TagUpdateSchema>;
export type Person = z.output<typeof PersonSchema>;
export type CreatePersonInput = z.input<typeof PersonCreateSchema>;
export type UpdatePersonInput = z.input<typeof PersonUpdateSchema>;
